import {
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import classNames from 'classnames';
import { format, parseISO } from 'date-fns';
import gql from 'graphql-tag';
import React, { useContext, useState } from 'react';

import { Alert, Copy } from 'src/common/ui/components';
import useTypedSWR from 'src/components/general/useTypedSWR';
import ConfirmDialog from 'src/components/pages/pageElements/confirmDialog';
import useTableStyles from 'src/components/pages/patientShow/documents/useTableStyles';
import { ApolloClientContext } from 'src/data/ApolloClientContext';
import { IdSubmissionDialog } from 'src/idVerification/components/IdSubmissionDialog';
import { IdVerificationStatus } from 'src/idVerification/components/IdVerificationStatus';
import { IdVerificationTitle } from 'src/idVerification/components/IdVerificationTitle';
import { GET_ID_VERIFICATIONS } from 'src/idVerification/queries/getIdVerifications.gql';
import { IdSubmission } from 'src/idVerification/types';

type IdVerificationTableProps = {
  patientId: string;
};

export const IdVerificationTable = ({ patientId }: IdVerificationTableProps) => {
  const styles = useStyles();
  const tableStyles = useTableStyles();
  const [currentSubmission, setCurrentSubmission] = useState<any>(null);
  const [deletingSubmissionId, setDeletingSubmissionId] = useState<any>(null);
  const [updateError, setUpdateError] = useState<Error | null>(null);
  const { apolloClient } = useContext(ApolloClientContext);

  const { data, mutate } = useTypedSWR([
    GET_ID_VERIFICATIONS,
    {
      patientId,
    },
  ]);

  const onEdit = (doc: IdSubmission) => {
    setCurrentSubmission(doc);
  };

  const handleDeleteConfirmation = async () => {
    if (!deletingSubmissionId) {
      return;
    }

    if (!apolloClient) {
      return;
    }

    try {
      await apolloClient.mutate({
        mutation: gql`
          mutation ($id: ID!) {
            staff_softDeleteSubmission(id: $id)
          }
        `,
        variables: {
          id: deletingSubmissionId,
        },
      });

      mutate();
    } catch (e) {
      setUpdateError(e);
    }

    setDeletingSubmissionId(null);
  };

  return (
    <>
      {updateError && (
        <Alert title="Error updating verification status" error={updateError} severity="error" />
      )}

      <Table className={tableStyles.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classNames(tableStyles.tableHead, styles.documentsColumn)}>
              Title
            </TableCell>
            <TableCell className={classNames(tableStyles.tableHead, styles.documentsColumn)}>
              Submitted Date
            </TableCell>
            <TableCell className={classNames(tableStyles.tableHead, styles.documentsColumn)}>
              Status
            </TableCell>
            <TableCell className={classNames(tableStyles.tableHead, styles.documentsColumn)} />
          </TableRow>
        </TableHead>
        <TableBody>
          {(data?.staff_idVerifications || []).map(doc => (
            <TableRow key={doc.id} className={tableStyles.tableRow}>
              <TableCell className={tableStyles.tableCell}>
                <IdVerificationTitle idVerification={doc} />
              </TableCell>
              <TableCell className={tableStyles.tableCell}>
                <Copy variant="body2">{format(parseISO(doc.createdAt), 'MMMM dd, yyyy')}</Copy>
              </TableCell>
              <TableCell className={tableStyles.tableCell}>
                <IdVerificationStatus idVerification={doc} />
              </TableCell>
              <TableCell>
                <menu className={classNames(tableStyles.tableActions, styles.tableActions)}>
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      onEdit(doc);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      setDeletingSubmissionId(doc.id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </menu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <IdSubmissionDialog
        isOpen={!!currentSubmission}
        onCancel={() => setCurrentSubmission(null)}
        setCurrentSubmission={setCurrentSubmission}
        submission={currentSubmission}
        mutate={mutate}
      />
      {deletingSubmissionId && (
        <ConfirmDialog
          isDestructive
          onSubmit={handleDeleteConfirmation}
          onCancel={() => setDeletingSubmissionId(null)}
          submitLabel="Delete"
        >
          Are you sure you want to delete this document?
        </ConfirmDialog>
      )}
    </>
  );
};

const useStyles = makeStyles({
  documentsColumn: {
    width: '25%',
  },
  tableActions: {
    display: 'flex',
    justifyContent: 'center',
  },
});
